import { Pressable, StyleSheet, View, ActivityIndicator } from "react-native";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import { MotiView } from "moti";
import CustomText from "../../common/general/CustomText/CustomText";
import { File } from "../../svgs/CustomCoursework";
import { Trash } from "@phosphor-icons/react/dist/csr/Trash";
import useFileFetch from "../../../hooks/useFileFetch";
import { File as FileType } from "@axio/graphql/generated/graphql";

interface ResourceCardProps {
  file: Partial<FileType>;
  showDate?: boolean;
  showDelete?: boolean;
  onDelete?: () => void;
  ingestionStatus?: "ingesting" | "failed" | undefined;
}

const ResourceCard = ({
  file,
  showDate = false,
  showDelete = false,
  onDelete = null,
  ingestionStatus,
}: ResourceCardProps) => {
  const { fetchAndOpenFile } = useFileFetch();

  const formattedDate = file.created && showDate ? new Date(file.created).toLocaleDateString() : null;

  // Determine status badge
  const statusBadge = ingestionStatus ? (
    <View
      style={[
        styles.statusBadge,
        ingestionStatus === "failed" ? styles.statusBadgeFailed : styles.statusBadgeIngesting,
      ]}>
      {ingestionStatus === "ingesting" && <ActivityIndicator size="small" color="#8B5CF6" />}
      <CustomText
        useTranslationText={false}
        text={ingestionStatus === "ingesting" ? "Processing..." : "Failed"}
        size="xs"
        style={{ color: ingestionStatus === "failed" ? "#EF4444" : "#8B5CF6", marginLeft: 4 }}
      />
    </View>
  ) : null;

  return (
    <MotiView
      from={{
        opacity: 0,
        translateX: -400,
      }}
      animate={{
        opacity: 1,
        translateX: 0,
      }}
      delay={200}
      transition={{
        type: "spring",
        damping: 18,
        stiffness: 120,
      }}>
      <View style={styles.resourceCard}>
        <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <Pressable
            onPress={() => fetchAndOpenFile(file.fileURL)}
            role="button"
            accessibilityLabel={`Open resource ${file.fileName}`}
            aria-label={`Open resource ${file.fileName}`}
            style={{ flexDirection: "column", flex: 1 }}>
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <File
                width={16}
                height={16}
                style={{ paddingRight: 4 }}
                accessible={false}
                accessibilityElementsHidden={true}
                importantForAccessibility="no-hide-descendants"
                aria-hidden={true}
              />
              <CustomText
                useTranslationText={false}
                numberOfLines={1}
                text={file.fileName}
                style={{ maxWidth: 200 }}
                weight="normal"
                size="s"
              />
            </View>
            {formattedDate && (
              <CustomText
                useTranslationText={false}
                text={`Added: ${formattedDate}`}
                style={{ color: "#667085", marginTop: 4 }}
                size="xs"
              />
            )}
            {statusBadge}
          </Pressable>
          {showDelete && onDelete && (
            <CustomButton
              styleType="transparent"
              onPress={onDelete}
              leftIcon={<Trash size={16} alt={`Delete icon for ${file.fileName}`} />}
              accessibilityLabel={`Delete resource ${file.fileName}`}
              aria-label={`Delete resource ${file.fileName}`}
            />
          )}
        </View>
      </View>
    </MotiView>
  );
};

export default ResourceCard;

const styles = StyleSheet.create({
  resourceCard: {
    width: 290,
    flexDirection: "column",
    padding: 10,
    backgroundColor: "#fff",
    borderColor: "#D9D9D9",
    borderWidth: 1,
    borderRadius: 10,
    justifyContent: "flex-start",
    marginBottom: 12,
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    alignSelf: "flex-start",
  },
  statusBadgeIngesting: {
    backgroundColor: "#F5F3FF",
  },
  statusBadgeFailed: {
    backgroundColor: "#FEF2F2",
  },
});
